export const PUBLIC_USER_ID_PATTERN = /^pwu_[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

// UI cache boundary only; the authenticated backend still authorizes every read/write.
export function getCustomerRenderScope(value) {
  const customer = Array.isArray(value) ? value[0] : value;
  const id = customer?.id ?? "";
  const publicId = firstPublicUserId(customer);
  return id || publicId ? JSON.stringify([String(id), publicId]) : "";
}

export const SELF_CUSTOMER_ID_FIELDS = [
  "user_id",
  "viewer_id",
  "sender_user_id",
  "payer_user_id",
  "follower_user_id",
  "from_user_id",
  "mateout_sender_user_id",
];

export const USER_TARGET_ID_FIELDS = [
  "receiver_user_id",
  "mateout_receiver_user_id",
  "to_user_id",
  "target_user_id",
  "target_profile_id",
  "candidate_user_id",
  "profile_user_id",
];

const PROFILE_PUBLIC_ID_KEYS = [
  "public_id",
  "publicId",
  "user_public_id",
  "userPublicId",
  "candidate_public_id",
  "candidatePublicId",
  "profile_public_id",
  "profilePublicId",
  "invited_by_public_id",
  "invitedByPublicId",
];

const RAW_CUSTOMER_ID_KEYS = [
  "raw_id",
  "rawId",
  "numeric_id",
  "numericId",
  "customer_id",
  "customerId",
];

export function isPublicUserId(value) {
  return PUBLIC_USER_ID_PATTERN.test(String(value || "").trim());
}

export function isRawNumericId(value) {
  return /^\d+$/.test(String(value || "").trim());
}

export function firstPublicUserId(...sources) {
  for (const source of sources) {
    if (!source) continue;
    if (typeof source === "string" && isPublicUserId(source)) {
      return source.trim();
    }
    if (typeof source !== "object") continue;
    for (const key of PROFILE_PUBLIC_ID_KEYS) {
      const value = String(source?.[key] || "").trim();
      if (isPublicUserId(value)) {
        return value;
      }
    }
    if (source?.user && typeof source.user === "object") {
      const nested = firstPublicUserId(source.user);
      if (nested) return nested;
    }
  }
  return "";
}

export function getStoredCustomerData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

export function getStoredCustomerPublicId() {
  return firstPublicUserId(getStoredCustomerData());
}

export function sanitizeCustomerUserData(userData, responseData = {}) {
  const source = Array.isArray(userData) ? userData[0] : userData;
  if (!source || typeof source !== "object") {
    return source || null;
  }

  // Resolve the identity to persist, in priority order:
  //   1. a signed public id (pwu_…) from the response or record's public_id fields
  //   2. an id already on the record that the API accepts — a pwu_ token or a
  //      numeric id (decodePublicUserId handles both)
  // If (and only if) none of those exist do we leave the id untouched. We must
  // NEVER delete a usable id: doing so is what silently logged users out of
  // authenticated calls ("Invalid User") after any non-login write to userData.
  const resolvedId =
    firstPublicUserId(responseData, responseData?.data, source) ||
    [source.id, source.user_id, source.public_id, source.publicId]
      .map((value) => String(value ?? "").trim())
      .find((value) => isPublicUserId(value) || isRawNumericId(value)) ||
    "";

  const sanitized = { ...source };
  for (const key of RAW_CUSTOMER_ID_KEYS) {
    delete sanitized[key];
  }

  if (resolvedId) {
    sanitized.id = resolvedId;
    sanitized.user_id = resolvedId;
    sanitized.public_id = resolvedId;
    sanitized.publicId = resolvedId;
  }
  // else: keep the record's existing fields as-is — never strip the identity.

  return sanitized;
}

// Single choke point for the ~15 places that write `userData`. Historically most
// did a raw `localStorage.setItem("userData", …)`, bypassing sanitisation — so an
// endpoint payload missing public_id fields would land unsanitised and then get
// its id wiped on the next normalize pass. This guard sanitises EVERY write to the
// `userData` key (current call sites and any future ones) so the stored identity
// is always a valid, consistent public id. Install once at app start.
let userDataStorageGuardInstalled = false;
export function installUserDataStorageGuard() {
  if (userDataStorageGuardInstalled || typeof window === "undefined") return;
  const proto = window.Storage && window.Storage.prototype;
  if (!proto || typeof proto.setItem !== "function") return;
  const nativeSetItem = proto.setItem;
  proto.setItem = function (key, value) {
    if (key === "userData") {
      try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        const sanitized = sanitizeCustomerUserData(parsed, parsed);
        if (sanitized) {
          return nativeSetItem.call(this, key, JSON.stringify(sanitized));
        }
      } catch {
        /* malformed value — fall through to the raw write */
      }
    }
    return nativeSetItem.call(this, key, value);
  };
  userDataStorageGuardInstalled = true;
}

export function normalizeCustomerUserDataInStorage() {
  const current = getStoredCustomerData();
  const sanitized = sanitizeCustomerUserData(current, current);
  if (sanitized) {
    localStorage.setItem("userData", JSON.stringify(sanitized));
  }
  return sanitized;
}

export function getProfilePath(target, options = {}) {
  const publicId = firstPublicUserId(target);
  const currentPublicId = getStoredCustomerPublicId();
  const allowSelf = options.allowSelf !== false;

  if (allowSelf && publicId && currentPublicId && publicId === currentPublicId) {
    return "/profile/self";
  }

  if (publicId) {
    return `/profile?public_id=${encodeURIComponent(publicId)}`;
  }

  if (allowSelf && options.self === true) {
    return "/profile/self";
  }

  return "";
}

export function withProfilePath(target, fallback = "#") {
  return getProfilePath(target) || fallback;
}

export function stripCustomerIdentityFields(value, context = {}) {
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return value;
    if ((text.startsWith("{") && text.endsWith("}")) || (text.startsWith("[") && text.endsWith("]"))) {
      try {
        return JSON.stringify(stripCustomerIdentityFields(JSON.parse(text), context));
      } catch {
        return value;
      }
    }
    if (text.includes("=")) {
      try {
        const params = new URLSearchParams(text);
        const sanitized = stripCustomerIdentityFields(params, context);
        return sanitized.toString();
      } catch {
        return value;
      }
    }
    return value;
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    SELF_CUSTOMER_ID_FIELDS.forEach((key) => value.delete(key));
    USER_TARGET_ID_FIELDS.forEach((key) => {
      const fieldValue = value.get(key);
      if (isRawNumericId(fieldValue)) {
        value.delete(key);
      }
    });
    if (String(value.get("followable_type") || "").toLowerCase() === "user" && isRawNumericId(value.get("followable_id"))) {
      value.delete("followable_id");
    }
    return value;
  }

  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
    SELF_CUSTOMER_ID_FIELDS.forEach((key) => value.delete(key));
    USER_TARGET_ID_FIELDS.forEach((key) => {
      if (isRawNumericId(value.get(key))) {
        value.delete(key);
      }
    });
    if (String(value.get("followable_type") || "").toLowerCase() === "user" && isRawNumericId(value.get("followable_id"))) {
      value.delete("followable_id");
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => stripCustomerIdentityFields(item, context));
  }

  const nextValue = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (SELF_CUSTOMER_ID_FIELDS.includes(key)) {
      return;
    }
    if (USER_TARGET_ID_FIELDS.includes(key) && isRawNumericId(entryValue)) {
      return;
    }
    if (key === "followable_id" && String(value.followable_type || "").toLowerCase() === "user" && isRawNumericId(entryValue)) {
      return;
    }
    if (entryValue && typeof entryValue === "object") {
      nextValue[key] = stripCustomerIdentityFields(entryValue, context);
      return;
    }
    nextValue[key] = entryValue;
  });
  return nextValue;
}

export function requestHasRawProfileUrl(url) {
  return /\/social\/profile\/\d+(?:[/?#]|$)/.test(String(url || ""));
}
